import { CheckOutlined, CloseOutlined, EditOutlined, EyeOutlined, LockOutlined, UploadOutlined } from "@ant-design/icons";

import { Button, Flex, Switch, Tooltip } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useToggleState } from "../common/hooks/useToggleState";
import useAuthority from "../permissions/hooks/useAuthority";
import {
  MODULES,
  PERMISSION_FEATURES,
} from "../permissions/types/permission-types";
import CustomerManagementComponent from "./customerManagement.component";
import useCustomerManagementApi from "../apiHooks/useCustomerManagementApi";
import { useNotificationContext } from "../core/contexts/NotificationContextProvider";
import {
  downloadFiles,
  getFileNameFromPresignedURL,
} from "../common/utils/common";

const CustomerManagement = () => {
  const [isCustomerModalOpen, toggleCustomerModal] = useToggleState(false);
  const [isResetPasswordModalOpen, toggleResetPasswordModal] =
    useToggleState(false);
  const [isDocumentReUploadModalOpen, toggleDocumentReUploadModalModal] =
    useToggleState(false);

  const [isViewNICModalOpen, setIsViewNICModalOpen] = useState(false);
  const [selectedNIC, setSelectedNIC] = useState(null);

  const {
    toggleCustomerStateApi: {
      execute: toggleCustomerState,
      isLoading: isTogglingCustomerState,
    },
    getNICOfCustomerApi: {
      execute: getNICOfCustomer,
      isLoading: isFetchingNIC,
    },
  } = useCustomerManagementApi();

  const { notify } = useNotificationContext();

  const customerTableRef = useRef();

  const customerModalRef = useRef();
  const resetPasswordModalRef = useRef();
  const docReUploadModalRef = useRef();

  const refreshTable = useCallback(
    () => customerTableRef.current.refresh(),
    []
  );
  const auth = useAuthority(MODULES.CUSTOMER_MANAGEMENT);
  const handleEdit = useCallback(
    (data) => {
      toggleCustomerModal();
      customerModalRef.current.getCustomerRecord(data);
    },
    [toggleCustomerModal]
  );

  const handleResetPassword = useCallback(
    (data) => {
      toggleResetPasswordModal();
      resetPasswordModalRef.current.setRecordId(data.customerID);
    },
    [toggleResetPasswordModal]
  );

  const handleDocumentUpload = useCallback(
    (data) => {
      toggleDocumentReUploadModalModal();
      docReUploadModalRef.current.setRecordId(data.customerID);
    },
    [toggleDocumentReUploadModalModal]
  );

  const handleCustomerToggleState = useCallback(
    async (checked, data) => {
      const res = await toggleCustomerState({
        isActive: checked,
        data: {
          CustomerID: data.customerID,
          IsActive: checked,
        },
      });
      if (res) {
        notify({
          message: `Customer marked as ${checked ? "Active" : "Inactive"}`,
        });
        refreshTable();
      }
    },
    [notify, refreshTable, toggleCustomerState]
  );

  const handleNICView = useCallback(
    async (data) => {
      const res = await getNICOfCustomer(data.customerID);
      if (res) {
        const viewableFileTypes = ["jpg", "jpeg", "png"];

        const uploadedFileType = getFileNameFromPresignedURL(res)
          .split(".")
          .at(-1);
        if (viewableFileTypes.includes(uploadedFileType)) {
          setSelectedNIC(res);
          setIsViewNICModalOpen(true);
          return;
        }

        downloadFiles(res);
      }
    },
    [getNICOfCustomer]
  );

  const actionCellRenderer = useCallback(
    (data) => {
      return (
        <Flex align="center" gap={8}>
          <Tooltip title={auth[PERMISSION_FEATURES.EDIT] ? "Edit" : "View"} placement="top">
            <Button
              onClick={() => handleEdit(data)}
              size="middle"
              type="default"
              icon={auth[PERMISSION_FEATURES.EDIT] ? <EditOutlined /> : <EyeOutlined />}
              style={{ borderRadius: 8, width: 34, padding: 0, borderColor: auth[PERMISSION_FEATURES.EDIT] ? "#86efac" : "#93c5fd", color: auth[PERMISSION_FEATURES.EDIT] ? "#22c55e" : "#3b82f6", transition: "all 0.18s ease" }}
              onMouseEnter={e => { const c = auth[PERMISSION_FEATURES.EDIT] ? "#22c55e" : "#3b82f6"; e.currentTarget.style.background = c; e.currentTarget.style.borderColor = c; e.currentTarget.style.color = "#ffffff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = auth[PERMISSION_FEATURES.EDIT] ? "#86efac" : "#93c5fd"; e.currentTarget.style.color = auth[PERMISSION_FEATURES.EDIT] ? "#22c55e" : "#3b82f6"; }}
            />
          </Tooltip>
          <Tooltip title="View NIC" placement="top">
            <Button
              size="middle"
              icon={<EyeOutlined />}
              onClick={() => handleNICView(data)}
              disabled={isFetchingNIC}
              style={{ borderRadius: 8, width: 34, padding: 0, borderColor: "#93c5fd", color: "#3b82f6", transition: "all 0.18s ease" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#3b82f6"; e.currentTarget.style.borderColor = "#3b82f6"; e.currentTarget.style.color = "#ffffff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.color = "#3b82f6"; }}
            />
          </Tooltip>
          {auth[PERMISSION_FEATURES.EDIT] && (
            <>
              <Tooltip title="Re-Upload NIC" placement="top">
                <Button
                  size="middle"
                  icon={<UploadOutlined />}
                  onClick={() => handleDocumentUpload(data)}
                  style={{ borderRadius: 8, width: 34, padding: 0, borderColor: "#c4b5fd", color: "#7c3aed", transition: "all 0.18s ease" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#7c3aed"; e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.color = "#ffffff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = "#c4b5fd"; e.currentTarget.style.color = "#7c3aed"; }}
                />
              </Tooltip>
              <Tooltip title="Reset Password" placement="top">
                <Button
                  onClick={() => handleResetPassword(data)}
                  size="middle"
                  type="default"
                  icon={<LockOutlined />}
                  style={{ borderRadius: 8, width: 34, padding: 0, borderColor: "#fde68a", color: "#d97706", transition: "all 0.18s ease" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#d97706"; e.currentTarget.style.borderColor = "#d97706"; e.currentTarget.style.color = "#ffffff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = "#fde68a"; e.currentTarget.style.color = "#d97706"; }}
                />
              </Tooltip>
              <Tooltip placement="top" title={data.isActive ? "Set Inactive" : "Set Active"}>
                <Switch
                  disabled={isTogglingCustomerState}
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  onChange={(checked) => handleCustomerToggleState(checked, data)}
                  defaultChecked={data.isActive}
                  key={data.customerID}
                  style={{ backgroundColor: data.isActive ? "#22c55e" : "#ef4444" }}
                />
              </Tooltip>
            </>
          )}
        </Flex>
      );
    },
    [
      auth,
      handleCustomerToggleState,
      handleDocumentUpload,
      handleEdit,
      handleNICView,
      handleResetPassword,
      isFetchingNIC,
      isTogglingCustomerState,
    ]
  );

  useEffect(() => {
    if (!isViewNICModalOpen) {
      setSelectedNIC(null);
    }
  }, [isViewNICModalOpen]);

  return (
    <CustomerManagementComponent
      actionCellRenderer={actionCellRenderer}
      isCustomerModalOpen={isCustomerModalOpen}
      toggleCustomerModal={toggleCustomerModal}
      refreshTable={refreshTable}
      customerTableRef={customerTableRef}
      customerModalRef={customerModalRef}
      auth={auth}
      isResetPasswordModalOpen={isResetPasswordModalOpen}
      toggleResetPasswordModal={toggleResetPasswordModal}
      resetPasswordModalRef={resetPasswordModalRef}
      isDocumentReUploadModalOpen={isDocumentReUploadModalOpen}
      toggleDocumentReUploadModalModal={toggleDocumentReUploadModalModal}
      docReUploadModalRef={docReUploadModalRef}
      isViewNICModalOpen={isViewNICModalOpen}
      setIsViewNICModalOpen={setIsViewNICModalOpen}
      selectedNIC={selectedNIC}
    />
  );
};

export default CustomerManagement;
